import {
  DateRange,
  ID,
  OrderDirection,
  priorityRanking,
  SortingMode,
  Task,
  TaskPriorityOptions,
  Timeslot,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { endOfDay, isAfter } from 'date-fns';

import { getTaskDueDate } from './get-task-due-date';
import { getDueTaskPlanning, getTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';

type TaskSortGeneratorOptions = {
  invertedTimeslots?: boolean;
};

// Code-unit string comparison. Unlike `localeCompare` (locale/ICU-dependent —
// V8 on web ships full ICU, Hermes on mobile has no Intl.Collator, so they
// disagree on mixed-case ids) this is identical on every JS engine.
const byCodeUnit = (valueA: ID, valueB: ID) => {
  if (valueA < valueB) {
    return -1;
  }
  if (valueA > valueB) {
    return 1;
  }
  return 0;
};

// Deterministic tie-break for tasks that are otherwise equal on the primary
// sort key. Without it the comparators return a constant for ties, which is an
// inconsistent comparator (cmp(a,b) === cmp(b,a)) whose result is
// implementation-defined — V8 (web) and Hermes (mobile) then order ties
// differently. createdAt-ascending, then code-unit id, is engine-independent.
const byCreatedAtThenId = (taskA: Task, taskB: Task) =>
  taskA.createdAt.getTime() - taskB.createdAt.getTime() ||
  byCodeUnit(taskA.id, taskB.id);

const taskSortGenerator =
  ({ invertedTimeslots }: TaskSortGeneratorOptions = {}) =>
  (
    taskA: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
    taskB: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
  ): number => {
    if (!taskA.sortDate || !taskB.sortDate) {
      if (taskA.sortDate) {
        return -1;
      }
      if (taskB.sortDate) {
        return 1;
      }
      return byCreatedAtThenId(taskA.task, taskB.task);
    }

    const dateDiff = taskA.sortDate.getTime() - taskB.sortDate.getTime();
    if (dateDiff !== 0) {
      return dateDiff;
    }

    const aStartTime = taskA.timeslot?.startTime;
    const bStartTime = taskB.timeslot?.startTime;

    if (isNumber(aStartTime) && isNumber(bStartTime)) {
      const timeDiff = invertedTimeslots
        ? bStartTime - aStartTime
        : aStartTime - bStartTime;
      return timeDiff || byCreatedAtThenId(taskA.task, taskB.task);
    }

    // timed before all-day
    if (isNumber(aStartTime)) {
      return -1;
    }
    if (isNumber(bStartTime)) {
      return 1;
    }

    return byCreatedAtThenId(taskA.task, taskB.task);
  };

export type DueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  invertedTimeslots?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

const getSortedTasksByDueDate = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    invertedTimeslots,
    expectedDueDate,
    dateRange,
  }: DueDateOptions,
) => {
  const endOfExpectedDate = endOfDay(expectedDueDate ?? today);
  const { deadlineTasks, dueDateTasks } = tasks.reduce<{
    deadlineTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
    dueDateTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
  }>(
    (acc, task) => {
      // dateRange resolves a scheduled task's time against its own occurrence, not today.
      if (dateRange && task.schedules) {
        const planning = getTaskPlanning(task, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          today,
          weekStartsOn,
          sinceLastEntryOnly: true,
          uncompletedOnly: true,
        });

        const plannedOccurrence = planning[0];
        const dueDate =
          plannedOccurrence?.date ??
          getTaskDueDate(task, { today, weekStartsOn });

        const timeslot = plannedOccurrence
          ? plannedOccurrence.timeslot
          : task.timeslot;

        if (!dueDate) {
          acc.dueDateTasks.push({
            task,
            timeslot,
          });
          return acc;
        }

        acc.dueDateTasks.push({
          task,
          sortDate: endOfDay(dueDate),
          timeslot,
        });
        return acc;
      }

      const planning = getDueTaskPlanning(task, {
        date: endOfExpectedDate,
        today,
        weekStartsOn,
      });
      const duePlanning = planning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, endOfExpectedDate),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const dueDate =
        plannedOccurrence?.date ??
        getTaskDueDate(task, { today, weekStartsOn });

      const timeslot = plannedOccurrence
        ? plannedOccurrence.timeslot
        : task.timeslot;

      if (!dueDate && !task.deadlineDeadline) {
        acc.dueDateTasks.push({
          task,
          timeslot,
        });
        return acc;
      }

      if (dueDate) {
        acc.dueDateTasks.push({
          task,
          sortDate: endOfDay(dueDate),
          timeslot,
        });
        return acc;
      }

      if (task.deadlineDeadline) {
        acc.deadlineTasks.push({
          task,
          sortDate: endOfDay(task.deadlineDeadline),
          timeslot,
        });
      }

      return acc;
    },
    { deadlineTasks: [], dueDateTasks: [] },
  );

  const sortFunc = taskSortGenerator({ invertedTimeslots });

  return [...deadlineTasks.sort(sortFunc), ...dueDateTasks.sort(sortFunc)].map(
    ({ task }) => task,
  );
};

export type PriorityOptions = {
  direction?: OrderDirection;
};

const getSortedTasksByPriority = (
  tasks: Task[],
  { direction = OrderDirection.Asc }: PriorityOptions,
) =>
  [...tasks].sort((taskA, taskB) => {
    const directionTaskA = direction === OrderDirection.Asc ? taskA : taskB;
    const directionTaskB = direction === OrderDirection.Asc ? taskB : taskA;

    const isNoneA =
      !directionTaskA.priority ||
      directionTaskA.priority === TaskPriorityOptions.None;
    const isNoneB =
      !directionTaskB.priority ||
      directionTaskB.priority === TaskPriorityOptions.None;

    if (isNoneA && isNoneB) {
      return byCreatedAtThenId(taskA, taskB);
    }
    if (isNoneA) {
      return 1;
    }
    if (isNoneB) {
      return -1;
    }

    return (
      priorityRanking[directionTaskA.priority!] -
        priorityRanking[directionTaskB.priority!] ||
      byCreatedAtThenId(taskA, taskB)
    );
  });

// Tasks present in `order` keep that order; tasks absent from it (e.g. freshly
// created or duplicated) are appended after, preserving their incoming order
// (the caller pre-sorts by createdAt). A plain comparator can't express this —
// returning a constant for absent ids is non-transitive and scatters them
// unpredictably — so partition explicitly.
const getSortedTasksByCustom = (tasks: Task[], order: ID[]) => {
  const orderIndex = new Map(order.map((id, index) => [id, index]));
  const known: Task[] = [];
  const unknown: Task[] = [];
  tasks.forEach((task) => {
    if (orderIndex.has(task.id)) {
      known.push(task);
      return;
    }
    unknown.push(task);
  });
  known.sort(
    (taskA, taskB) =>
      (orderIndex.get(taskA.id) ?? 0) - (orderIndex.get(taskB.id) ?? 0),
  );
  return [...known, ...unknown];
};

export type SortedTasksOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  direction?: OrderDirection;
  mode?: SortingMode[];
  order?: ID[];
  invertedTimeslots?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

export const getSortedTasks = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    direction,
    mode = [],
    order = [],
    invertedTimeslots,
    expectedDueDate,
    dateRange,
  }: SortedTasksOptions,
) => {
  // Base ordering so ties on the active sort mode resolve deterministically.
  let sortedTasks = [...tasks].sort(byCreatedAtThenId);

  mode?.forEach((mode) => {
    switch (mode) {
      case SortingMode.DueDate:
        sortedTasks = getSortedTasksByDueDate(sortedTasks, {
          today,
          weekStartsOn,
          invertedTimeslots,
          expectedDueDate,
          dateRange,
        });
        break;
      case SortingMode.DeadlineDeadline:
        sortedTasks = [...sortedTasks].sort((taskA, taskB) => {
          if (!taskA.deadlineDeadline && !taskB.deadlineDeadline) {
            return byCreatedAtThenId(taskA, taskB);
          }
          if (!taskA.deadlineDeadline) {
            return 1;
          }
          if (!taskB.deadlineDeadline) {
            return -1;
          }
          return (
            taskA.deadlineDeadline.getTime() -
              taskB.deadlineDeadline.getTime() ||
            byCreatedAtThenId(taskA, taskB)
          );
        });
        break;
      case SortingMode.Priority:
        sortedTasks = getSortedTasksByPriority(sortedTasks, { direction });
        break;
      case SortingMode.Name:
        sortedTasks = [...sortedTasks].sort(
          (a, b) => a.name.localeCompare(b.name) || byCreatedAtThenId(a, b),
        );
        break;
      case SortingMode.DeletedAt:
        sortedTasks = [...sortedTasks].sort((taskA, taskB) => {
          if (!taskA.deletedAt && !taskB.deletedAt) {
            return byCreatedAtThenId(taskA, taskB);
          }
          if (!taskA.deletedAt) {
            return 1;
          }
          if (!taskB.deletedAt) {
            return -1;
          }
          return (
            taskB.deletedAt.getTime() - taskA.deletedAt.getTime() ||
            byCreatedAtThenId(taskA, taskB)
          );
        });
        break;
      case SortingMode.Custom:
        sortedTasks = getSortedTasksByCustom(sortedTasks, order);
        break;
    }
  });

  return sortedTasks;
};
